import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";

/*
   Modal: admin cadastra um NOVO CORRETOR AUTÔNOMO
   Após criar, mostra o link de setup + senha temporária pra o admin repassar
   caso queira. Endpoint envia por WhatsApp automaticamente.
*/
export function CreateRealtorDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", plan: "solo" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/create-realtor", form);
      setResult(data);
      toast.success("Corretor criado com sucesso");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao criar corretor");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", plan: "solo" });
    setResult(null);
    setCopied(false);
  };

  const copyLink = () => {
    if (!result?.setup_link) return;
    navigator.clipboard.writeText(result.setup_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-create-realtor">
        <DialogHeader>
          <DialogTitle>Novo corretor autônomo</DialogTitle>
          <DialogDescription>
            Cria uma conta de corretor sem vínculo com imobiliária. O link de setup
            de senha vai ser enviado por WhatsApp automaticamente e também mostrado aqui.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm">
              <div className="font-semibold text-green-900">Corretor cadastrado ✓</div>
              <div className="text-green-800 mt-1">{result.name} · {result.email}</div>
              <div className="text-xs text-green-700 mt-1">
                WhatsApp: {result.phone} · {result.wa_sent ? "mensagem enviada" : "envio manual necessário"}
              </div>
            </div>
            <div>
              <Label>Link pra definir senha (válido até uso)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={result.setup_link} readOnly className="text-xs font-mono" data-testid="realtor-setup-link" />
                <Button type="button" variant="outline" size="icon" onClick={copyLink} data-testid="copy-setup-link">
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="text-xs text-stone-500">
              Senha temporária (caso ele prefira usar antes de setar a definitiva): <strong className="font-mono">{result.temp_password}</strong>
            </div>
            <DialogFooter>
              <Button onClick={() => { reset(); onOpenChange(false); }} data-testid="close-realtor-dialog">Fechar</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nome completo</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="João da Silva" data-testid="input-realtor-name" />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="joao@email.com" data-testid="input-realtor-email" />
            </div>
            <div>
              <Label>WhatsApp (com DDD)</Label>
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="48999998888" data-testid="input-realtor-phone" />
            </div>
            <div>
              <Label>Plano inicial</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="select-realtor-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solo">Solo (R$ 67/mês)</SelectItem>
                  <SelectItem value="dupla">Dupla (R$ 117/mês)</SelectItem>
                  <SelectItem value="equipe">Equipe (R$ 247/mês)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={loading} data-testid="submit-create-realtor">
                {loading ? "Criando…" : "Criar corretor"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}


/*
   Modal: admin cadastra uma NOVA IMOBILIÁRIA + primeiro gestor
   Igual acima, mas cria também a agência com plano/limite.
*/
export function CreateAgencyDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    agency_name: "", city: "", state: "SC",
    admin_name: "", admin_email: "", admin_phone: "",
    plan: "starter",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/create-agency", form);
      setResult(data);
      toast.success("Imobiliária criada com sucesso");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao criar imobiliária");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setForm({ agency_name: "", city: "", state: "SC", admin_name: "", admin_email: "", admin_phone: "", plan: "starter" });
    setResult(null);
    setCopied(false);
  };

  const copyLink = () => {
    if (!result?.setup_link) return;
    navigator.clipboard.writeText(result.setup_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-create-agency">
        <DialogHeader>
          <DialogTitle>Nova imobiliária</DialogTitle>
          <DialogDescription>
            Cria a imobiliária + a conta do gestor responsável. O gestor recebe um
            link de setup de senha por WhatsApp pra começar a cadastrar corretores.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm">
              <div className="font-semibold text-green-900">Imobiliária cadastrada ✓</div>
              <div className="text-green-800 mt-1">{result.agency_name}</div>
              <div className="text-xs text-green-700 mt-1">
                Plano: <strong>{result.plan}</strong> · limite de {result.max_realtors} corretores
              </div>
              <div className="text-xs text-green-700 mt-1">
                Gestor: {result.admin.name} · {result.admin.email}
              </div>
              <div className="text-xs text-green-700 mt-0.5">
                WhatsApp gestor: {result.admin.phone} · {result.wa_sent ? "mensagem enviada" : "envio manual necessário"}
              </div>
            </div>
            <div>
              <Label>Link pra o gestor definir senha</Label>
              <div className="flex gap-2 mt-1">
                <Input value={result.setup_link} readOnly className="text-xs font-mono" data-testid="agency-setup-link" />
                <Button type="button" variant="outline" size="icon" onClick={copyLink} data-testid="copy-agency-setup-link">
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="text-xs text-stone-500">
              Senha temporária: <strong className="font-mono">{result.temp_password}</strong>
            </div>
            <DialogFooter>
              <Button onClick={() => { reset(); onOpenChange(false); }} data-testid="close-agency-dialog">Fechar</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome da imobiliária</Label>
                <Input required value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                  placeholder="Ex: Imobiliária XPTO" data-testid="input-agency-name" />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Florianópolis" data-testid="input-agency-city" />
              </div>
              <div>
                <Label>Estado (UF)</Label>
                <Input required maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                  placeholder="SC" data-testid="input-agency-state" />
              </div>
            </div>
            <div className="pt-2 border-t">
              <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-semibold">Gestor(a) responsável</div>
              <div className="space-y-3">
                <div>
                  <Label>Nome</Label>
                  <Input required value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    placeholder="Fernanda Silva" data-testid="input-agency-admin-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>E-mail</Label>
                    <Input required type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      placeholder="gestor@imob.com" data-testid="input-agency-admin-email" />
                  </div>
                  <div>
                    <Label>WhatsApp</Label>
                    <Input required value={form.admin_phone} onChange={(e) => setForm({ ...form, admin_phone: e.target.value })}
                      placeholder="48999998888" data-testid="input-agency-admin-phone" />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Label>Plano da imobiliária</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="select-agency-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (até 3 corretores)</SelectItem>
                  <SelectItem value="pro">Pro (até 10 corretores)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (até 30 corretores)</SelectItem>
                  <SelectItem value="custom">Custom (ilimitado)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={loading} data-testid="submit-create-agency">
                {loading ? "Criando…" : "Criar imobiliária"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
